import * as React from 'react';
import {
  ITrendReportState,
  trendReportProvider,
} from '../../../data/providers/reports/trendReportProvider';
import { Entity, EntityType } from '../../../data/types';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { EPdfMode, isPdf, Pdf } from '../../shared/pdf/Pdf';
import { PdfContainer } from '../../shared/pdf/PdfContainer';
import { ErrorMessage } from '../utils/ErrorMessages';
import { ReportOverview } from '../utils/ReportOverview';
import { useFilename } from '../utils/useFilename';
import { TrendSettings } from './settings/Settings';
import { Trends } from './Trends';

interface IProps {
  pdfMode?: EPdfMode;
  entity: Entity;
  entityType: EntityType;
  onPdfModeChange: (pdfMode: EPdfMode) => void;
  loading: boolean;
  setLoading: (v: boolean) => void;
}

export const TrendReport = (props: IProps): React.ReactElement => {
  const [reportState, setReportState] = React.useState<ITrendReportState>();
  /**
   * @description subscribe to report state
   */
  React.useEffect((): (() => void) => {
    props.setLoading(true);
    const subscriber = trendReportProvider(
      props.entityType,
      props.entity.id
    ).subscribe((state: ITrendReportState): void => {
      setReportState(state);
      props.setLoading(false);
    });
    return (): void => subscriber.unsubscribe();
  }, [props.entity?.id]);

  /**
   * @description make filename for export
   */
  const filename = useFilename('trend', props.entity, reportState?.timeframe);

  return (
    <React.Fragment>
      {props.loading ? (
        <LinearLoading />
      ) : reportState?.isValid ? (
        isPdf(props.pdfMode) ? (
          <PdfContainer>
            <Pdf
              onComplete={(): void => {
                return null;
              }}
              filename={filename}
              title='Trend Report'
              entity={props.entity}
              loading={props.loading}
              reportState={reportState}
              onPdfModeChange={props.onPdfModeChange}
              pdfMode={props.pdfMode}
            >
              <ReportOverview
                reportName={`Trend Report - ${reportState.settings.name}`}
                noComparison
                isPdf={isPdf(props.pdfMode)}
              />
              <Trends state={reportState} pdfMode={props.pdfMode} />
            </Pdf>
          </PdfContainer>
        ) : (
          <React.Fragment>
            <ReportOverview
              reportName={`Trend Report - ${reportState.settings.name}`}
              noComparison
              isPdf={isPdf(props.pdfMode)}
            />
            <Trends state={reportState} pdfMode={props.pdfMode} />
          </React.Fragment>
        )
      ) : (
        <Error
          errorCode={reportState?.errorCode}
          setLoading={props.setLoading}
        />
      )}
    </React.Fragment>
  );
};

const Error = ({
  errorCode,
  setLoading,
}: {
  errorCode?: string;
  setLoading: (v: boolean) => void;
}): React.ReactElement => {
  const onSave = (): void => setLoading(true);
  switch (errorCode) {
    case 'missing-template':
      return (
        <ErrorMessage iconName='window' text='Please Select a Template'>
          <TrendSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );
    case 'missing-metrics':
      return (
        <ErrorMessage iconName='poll' text='Please Select Metrics'>
          <TrendSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );

    case 'missing-date-range':
      return (
        <ErrorMessage
          iconName='schedule'
          text='Please select a valid timeframe for this report'
        >
          <TrendSettings
            style={{ marginTop: '10px', maxWidth: 'max-content' }}
            onSave={onSave}
          />
        </ErrorMessage>
      );

    case 'insufficient-data':
      return (
        <ErrorMessage iconName='error_outline' text='Not Enough Data'>
          <div style={{ marginTop: '10px' }}>
            There is not enough data during the selected time period to produce
            this report. Try changing the timeframe in the settings.
          </div>
          <TrendSettings
            onSave={onSave}
            style={{ maxWidth: 'max-content', marginTop: '10px' }}
          />
        </ErrorMessage>
      );
    default:
      return null;
  }
};
